import React, { useState, useEffect } from 'react';
import { X, Loader, AlertCircle, CheckCircle, UserPlus, LogIn, Mail, ArrowLeft, Stethoscope } from 'lucide-react';
import {
  login,
  registerUserWithLeadFlow,
  checkEmailAvailability,
  checkPhoneAvailability,
  createUserWithFirebaseUID,
  requestPasswordReset
} from '../services/authService';
import {
  createUserInFirebase,
  socialSignInFirebase,
  fetchDaisUserByFirebaseUid
} from '../services/FirebaseService';
import { saveUserSession } from '../utils/checkoutHelpers';
import CountryCodeSelect from './CountryCodeSelect';

// El alta pide solo nombre, email y teléfono, pero Firebase Auth exige una
// contraseña para crear la cuenta. La generamos de forma segura y transparente
// para la usuaria (luego puede establecer una vía "olvidé mi contraseña").
const generateSecurePassword = () => {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  const arr = new Uint32Array(16);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  let pwd = '';
  for (let i = 0; i < 14; i++) pwd += letters[arr[i] % letters.length];
  pwd += digits[arr[14] % digits.length];
  pwd += digits[arr[15] % digits.length];
  return pwd; // 16 chars, garantiza letras + dígitos (cumple el regex del backend)
};

const AuthModal = ({
  isOpen, 
  onClose, 
  onSuccess, 
  initialStep = 'login', 
  specialty,
  utmParams = {},
  selectedSlot = null,
  specialtyConfig = {},
  selectedSpecialty = null,
  firebaseUID = null, // Nuevo parámetro para Firebase UID
  initialEmail = '', // Nuevo: email prellenado
  forceRegistration = false // Nuevo: forzar registro
}) => {

  useEffect(() => {
    if (initialEmail) {
      setFormData(prev => ({ ...prev, email: initialEmail }));
    }
    
    // Si forceRegistration es true, cambiar a paso de registro
    if (forceRegistration && step === 'login') {
      setStep('register');
    }
  }, [initialEmail, forceRegistration]);

  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    telefono: '',
    phoneCountry: 'MX',
    phoneCode: '+52',
    countryPhoneId: 156, // res.country.id de México en Odoo
    birthDate: '',
    birthSex: 'female',
    pronoun: '1'
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showEmailCheck, setShowEmailCheck] = useState(false);
  const [showPhoneCheck, setShowPhoneCheck] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Sesión de Google/Apple pendiente de completar registro (uid + token del
  // proveedor). Cuando existe, handleRegister crea el dais.user con este uid
  // en lugar de crear cuenta nueva en Firebase Auth.
  const [socialAuth, setSocialAuth] = useState(null);
  const [socialLoading, setSocialLoading] = useState(null);

  if (!isOpen) return null;

  const socialButtons = (
    <div className="space-y-2.5 mb-4">
      <button
        type="button"
        onClick={() => handleSocial('google')}
        disabled={loading || socialLoading !== null}
        className="w-full flex items-center justify-center gap-2.5 rounded-full px-6 py-2.5 text-sm font-semibold bg-white text-text-strong border border-brand/20 hover:bg-warm-canvas transition-colors disabled:opacity-60"
      >
        {socialLoading === 'google' ? (
          <Loader className="animate-spin" size={15} />
        ) : (
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
        )}
        Continuar con Google
      </button>
      <button
        type="button"
        onClick={() => handleSocial('apple')}
        disabled={loading || socialLoading !== null}
        className="w-full flex items-center justify-center gap-2.5 rounded-full px-6 py-2.5 text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-60"
      >
        {socialLoading === 'apple' ? (
          <Loader className="animate-spin" size={15} />
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.86-3.08.38-1.09-.5-2.08-.53-3.2 0-1.44.62-2.2.44-3.06-.38C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
        )}
        Continuar con Apple
      </button>
      <div className="flex items-center gap-3 pt-1" aria-hidden="true">
        <div className="flex-1 h-px bg-brand/20" />
        <span className="text-[11px] font-bold uppercase tracking-wide text-text-strong/50">
          o con tu correo
        </span>
        <div className="flex-1 h-px bg-brand/20" />
      </div>
    </div>
  );

  // "Continuar con Google/Apple": si la usuaria ya existe inicia sesión
  // directo; si no, prellena el registro (solo faltan teléfono y nombre).
  const handleSocial = async (providerName) => {
    if (socialLoading || loading) return;
    setError('');
    setSocialLoading(providerName);
    try {
      const fb = await socialSignInFirebase(providerName);
      if (!fb.success) {
        if (fb.errorCode !== 'cancelled') setError(fb.error);
        return;
      }
      const daisUser = await fetchDaisUserByFirebaseUid(fb.firebaseUid);
      if (daisUser?.id) {
        // Usuaria conocida: la sesión se arma con el doc de Firestore + el ID
        // token de Firebase (mismo patrón que el landing). Las llamadas a Odoo
        // se autentican con ese token vía el bridge.
        saveUserSession(
          {
            userId: Number(daisUser.id),
            authToken: fb.firebaseToken,
            firebaseUid: fb.firebaseUid
          },
          {
            nombre: daisUser.name || fb.displayName || '',
            apellido: daisUser.last_name || '',
            email: daisUser.email || fb.email,
            telefono: daisUser.phone || '',
            leadId: daisUser.lead_id ?? null
          }
        );
        onSuccess();
        return;
      }
      // Usuaria nueva: completar registro con los datos del proveedor.
      setSocialAuth({ uid: fb.firebaseUid, token: fb.firebaseToken });
      setFormData(prev => ({
        ...prev,
        email: fb.email,
        nombre: fb.displayName || prev.nombre
      }));
      setStep('register');
    } finally {
      setSocialLoading(null);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email.includes('@')) {
      setError('Ingresa un correo electrónico válido');
      return;
    }
    setLoading(true);
    try {
      const result = await requestPasswordReset(formData.email);
      if (result.success) {
        setResetSent(true);
      } else {
        setError(result.error || 'No pudimos enviar el correo. Intenta de nuevo.');
      }
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const goToReset = () => {
    setStep('reset');
    setError('');
    setResetSent(false);
  };

  const backToLogin = () => {
    setStep('login');
    setError('');
    setResetSent(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setValidationErrors({});

    // Validaciones básicas
    if (!formData.email.includes('@')) {
      setError('Ingresa un correo electrónico válido');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Persistir la sesión para que funcione el dashboard logueado
        saveUserSession(
          {
            userId: result.user?.id || result.firebaseUid,
            authToken: result.token,
            firebaseUid: result.firebaseUid || result.user?.firebaseUid || ''
          },
          {
            nombre: result.user?.nombre || result.user?.name || '',
            apellido: result.user?.apellido || '',
            email: result.user?.email || formData.email,
            telefono: result.user?.telefono || result.user?.phone || '',
            birthDate: result.user?.birthDate || '',
            birthSex: result.user?.birthSex || '',
            pronoun: result.user?.pronoun || ''
          }
        );
        onSuccess();
      } else {
        // Verificar si es error de usuario no encontrado
        if (result.errorType === 'not_found') {
          setError('No se encontró una cuenta con este correo. ¿Quieres registrarte?');
          setTimeout(() => {
            setStep('register');
            setError('');
          }, 1500);
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setValidationErrors({});

    // Validaciones — el alta solo pide nombre, email y teléfono.
    const errors = {};

    if (!formData.email.includes('@') || formData.email.length > 100) {
      errors.email = 'El correo debe contener @ y tener máximo 100 caracteres';
    }

    const cleanPhone = formData.telefono.replace(/\D/g, '');
    if (formData.phoneCountry === 'MX') {
      if (!/^\d{10}$/.test(cleanPhone)) {
        errors.telefono = 'El teléfono debe tener exactamente 10 dígitos';
      }
    } else if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      errors.telefono = 'Ingresa un número de teléfono válido';
    }

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setLoading(false);
      return;
    }

    // Contraseña autogenerada (campo oculto): los flujos de Firebase/lead la requieren.
    const generatedPassword = generateSecurePassword();

    // Uid ya autenticado: viene por prop (flujo checkup) o de Google/Apple.
    const effectiveFirebaseUID = firebaseUID || socialAuth?.uid;

    try {
      // Si tenemos firebaseUID, usar el nuevo flujo
      if (effectiveFirebaseUID) {
        setLoading(true);

        const result = await createUserWithFirebaseUID({
          email: formData.email,
          firebase_uid: effectiveFirebaseUID,
          firebase_token: socialAuth?.token,
          nombre: formData.nombre,
          apellido: formData.apellido,
          telefono: formData.telefono,
          phoneCountry: formData.phoneCountry || 'MX',
          countryPhoneId: formData.countryPhoneId,
          birthDate: formData.birthDate,
          birthSex: formData.birthSex,
          pronoun: formData.pronoun,
          utmParams,
          selectedSlot,
          specialtyConfig,
          selectedSpecialty
        });
        
        if (result.success) {
          // Con social sign-in la sesión se persiste aquí mismo (no hay flujo
          // posterior de login que lo haga, como sí ocurre en el flujo checkup).
          if (socialAuth) {
            saveUserSession(
              {
                userId: result.user?.id || effectiveFirebaseUID,
                authToken: result.token || socialAuth.token,
                firebaseUid: effectiveFirebaseUID
              },
              {
                nombre: formData.nombre,
                apellido: formData.apellido,
                email: formData.email,
                telefono: formData.telefono,
                phoneCountry: formData.phoneCountry,
                phoneCode: formData.phoneCode,
                birthDate: formData.birthDate,
                birthSex: formData.birthSex,
                pronoun: formData.pronoun
              }
            );
            onSuccess({ isNewUser: true });
          } else {
            onSuccess();
          }
        } else {
          setError(result.error);

          // Si el usuario ya existe, ofrecer iniciar sesión
          if (result.errorType === 'duplicate') {
            setTimeout(() => {
              setStep('login');
              setError('Este correo ya está registrado. Por favor inicia sesión.');
            }, 1000);
          }
        }
        setLoading(false);
        return;
      }

      // Flujo antiguo (sin Firebase UID)
      setShowEmailCheck(true);
      const isEmailAvailable = await checkEmailAvailability(formData.email);
      setShowEmailCheck(false);

      if (!isEmailAvailable) {
        setError('Este correo electrónico ya está registrado. ¿Ya tienes cuenta?');
        setTimeout(() => {
          setStep('login');
          setError('Por favor inicia sesión con tu cuenta existente.');
        }, 1000);
        setLoading(false);
        return;
      }

      setShowPhoneCheck(true);
      const isPhoneAvailable = await checkPhoneAvailability(formData.telefono);
      setShowPhoneCheck(false);

      if (!isPhoneAvailable) {
        setError('Este número de teléfono ya está registrado. ¿Ya tienes cuenta?');
        setTimeout(() => {
          setStep('login');
          setError('Por favor inicia sesión con tu cuenta existente.');
        }, 1000);
        setLoading(false);
        return;
      }

      // Decidir flujo según si hay slot+especialidad para reservar:
      // - Con slot: usar el flujo legacy crm.lead → process_lead_to_user (registerUserWithLeadFlow)
      // - Sin slot (login modal genérico, SOS sin slot, etc.): crear primero en Firebase Auth
      //   y luego en Odoo con createUserWithFirebaseUID. El flujo legacy con slot=null tiraba
      //   "Configuración de especialidad no encontrada" que se reportaba como "Error de conexión".
      const hasBookingContext = !!(selectedSlot && selectedSpecialty && specialtyConfig?.[selectedSpecialty]);

      if (!hasBookingContext) {
        // 1) Crear el usuario en Firebase Auth
        const fbResult = await createUserInFirebase({
          email: formData.email,
          password: generatedPassword,
        });

        if (!fbResult.success) {
          // Mapear códigos comunes de Firebase a mensajes claros
          const code = fbResult.errorCode || '';
          let msg = fbResult.error || 'No pudimos crear tu cuenta.';
          if (code === 'auth/email-already-in-use') {
            msg = 'Este correo ya está registrado. Por favor inicia sesión.';
            setTimeout(() => { setStep('login'); setError(msg); }, 1000);
          } else if (code === 'auth/weak-password') {
            msg = 'La contraseña es muy débil. Usa al menos 8 caracteres con letras y números.';
          } else if (code === 'auth/invalid-email') {
            msg = 'El correo electrónico no es válido.';
          } else if (code === 'auth/network-request-failed') {
            msg = 'Sin conexión a internet. Revisa tu red e intenta de nuevo.';
          }
          setError(msg);
          setLoading(false);
          return;
        }

        // 2) Crear el usuario en Odoo enlazado al Firebase UID.
        // El controller de Odoo valida el auth_token contra Firebase, así que pasamos
        // el ID token recién emitido (createUserInFirebase ya lo devuelve en firebaseToken).
        const odooResult = await createUserWithFirebaseUID({
          email: formData.email,
          firebase_uid: fbResult.firebaseUid,
          firebase_token: fbResult.firebaseToken,
          nombre: formData.nombre,
          apellido: formData.apellido,
          telefono: formData.telefono,
          phoneCountry: formData.phoneCountry || 'MX',
          countryPhoneId: formData.countryPhoneId, // id real de res.country (MX=156)
          birthDate: formData.birthDate,
          birthSex: formData.birthSex,
          pronoun: formData.pronoun,
          utmParams,
          // Sin slot/especialidad — createUserWithFirebaseUID ya maneja este caso
          selectedSlot: null,
          specialtyConfig: {},
          selectedSpecialty: null,
        });

        if (odooResult.success) {
          // Persistir la sesión para que el dashboard logueado funcione sin recargar.
          // Sin esto el usuario quedaba "creado pero no logueado" (bug reportado 2026-05-06).
          saveUserSession(
            {
              userId: odooResult.user?.id || fbResult.firebaseUid,
              authToken: odooResult.token || fbResult.firebaseToken,
              firebaseUid: fbResult.firebaseUid,
            },
            {
              nombre: formData.nombre,
              apellido: formData.apellido,
              email: formData.email,
              telefono: formData.telefono,
              phoneCountry: formData.phoneCountry,
              phoneCode: formData.phoneCode,
              birthDate: formData.birthDate,
              birthSex: formData.birthSex,
              pronoun: formData.pronoun,
            }
          );
          onSuccess({ isNewUser: true });
        } else {
          setError(odooResult.error || 'No se pudo completar el registro. Intenta de nuevo.');
        }
        setLoading(false);
        return;
      }

      // Hay slot + especialidad: usar el flujo legacy con lead
      const result = await registerUserWithLeadFlow(
        { ...formData, password: generatedPassword },
        utmParams,
        selectedSlot,
        specialtyConfig,
        selectedSpecialty
      );

      if (result.success) {
        onSuccess();
      } else {
        setError(result.error);

        if (result.errorType === 'email' || result.errorType === 'phone') {
          setTimeout(() => {
            setStep('login');
            setError(`Este ${result.errorType === 'email' ? 'correo' : 'teléfono'} ya está registrado. Por favor inicia sesión.`);
          }, 1000);
        } else if (result.errorType === 'lead') {
          setError('Hubo un problema al crear tu cuenta. Por favor intenta de nuevo.');
        }
      }
    } catch (error) {
      console.error('[AuthModal] handleRegister error:', error);
      setError(error?.message || 'No pudimos crear tu cuenta. Intenta de nuevo.');
    } finally {
      setLoading(false);
      setShowEmailCheck(false);
      setShowPhoneCheck(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (error) setError('');
  };

  const toggleStep = () => {
    setStep(step === 'login' ? 'register' : 'login');
    setError('');
    setValidationErrors({});
    setFormData({
      email: '',
      password: '',
      nombre: '',
      apellido: '',
      telefono: '',
      phoneCountry: 'MX',
      phoneCode: '+52',
      countryPhoneId: 156,
      birthDate: '',
      birthSex: 'female',
      pronoun: '1'
    });
  };

  // Selección de país (lada): guardamos code/phone_code/id real para enviar el
  // country_phone_id correcto al backend. Limpiamos el teléfono al cambiar de país.
  const handleCountrySelect = (country) => {
    setFormData((prev) => ({
      ...prev,
      phoneCountry: country.code,
      phoneCode: country.phone_code,
      countryPhoneId: country.id,
      telefono: prev.phoneCountry === country.code ? prev.telefono : '',
    }));
    if (validationErrors.telefono) {
      setValidationErrors((prev) => ({ ...prev, telefono: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-elevated max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-text-primary">
                {step === 'login' ? 'Inicia sesión' : step === 'reset' ? '¿Olvidaste tu contraseña?' : 'Crea tu cuenta'}
              </h2>
              <p className="text-text-body">
                {step === 'login'
                  ? 'Para continuar con tu reserva'
                  : step === 'reset'
                    ? 'Te enviaremos por correo un enlace para crear una nueva'
                    : 'Para reservar con tu doctora preferida'}
              </p>
              {firebaseUID && (
                <p className="text-sm text-brand mt-1">
                  Usando autenticación de Firebase
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-text-mute hover:text-text-strong transition-colors"
              disabled={loading}
            >
              <X size={24} />
            </button>
          </div>

          {/* Specialty info */}
          {specialty && (
            <div className="mb-6 p-4 bg-brand-tint rounded-xl">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white text-brand shrink-0">
                  <Stethoscope size={24} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm text-text-body">Estás reservando:</p>
                  <p className="font-bold text-text-primary">{specialty.name}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-red-500 mt-0.5 flex-shrink-0" size={18} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Forms */}
          {step === 'reset' ? (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              {resetSent ? (
                <div className="text-center space-y-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-mint-pill flex items-center justify-center">
                    <CheckCircle className="text-mint-deep" size={28} />
                  </div>
                  <p className="text-text-body text-sm">
                    Si <span className="font-semibold">{formData.email}</span> tiene una cuenta,
                    te enviamos un correo con el enlace para restablecer tu contraseña.
                    Revisa tu bandeja de entrada y la carpeta de spam.
                  </p>
                  <button
                    type="button"
                    onClick={backToLogin}
                    className="w-full py-3 bg-daisies text-white rounded-card font-semibold hover:bg-daisies-hover transition-all"
                  >
                    Volver a iniciar sesión
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-text-strong mb-1">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-3 py-2 bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-daisies text-white rounded-card font-semibold hover:bg-daisies-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Mail size={20} />
                        Enviar enlace
                      </>
                    )}
                  </button>

                  <div className="text-center pt-4 border-t border-brand-100">
                    <button
                      type="button"
                      onClick={backToLogin}
                      className="text-daisies hover:text-daisies-hover text-sm font-medium inline-flex items-center gap-1"
                      disabled={loading}
                    >
                      <ArrowLeft size={16} />
                      Volver a iniciar sesión
                    </button>
                  </div>
                </>
              )}
            </form>
          ) : step === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              {socialButtons}
              <div>
                <label className="block text-sm font-medium text-text-strong mb-1">
                  Correo electrónico *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-strong mb-1">
                  Contraseña *
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-3 py-2 bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  placeholder="Tu contraseña"
                />
                <div className="text-right mt-1">
                  <button
                    type="button"
                    onClick={goToReset}
                    className="text-daisies hover:text-daisies-hover text-sm font-medium"
                    disabled={loading}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-daisies text-white rounded-card font-semibold hover:bg-daisies-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Iniciando sesión...
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    Iniciar sesión
                  </>
                )}
              </button>

              <div className="text-center pt-4 border-t border-brand-100">
                <button
                  type="button"
                  onClick={toggleStep}
                  className="text-daisies hover:text-daisies-hover text-sm font-medium"
                  disabled={loading}
                >
                  ¿No tienes cuenta? Regístrate aquí
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              {socialButtons}
              {firebaseUID ? (
                <div className="p-3 bg-brand-tint rounded-lg mb-4">
                  <p className="text-sm text-brand-deep">
                    <strong>Nota:</strong> Estás usando autenticación de Firebase. 
                    Solo necesitas completar tus datos personales.
                  </p>
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-text-strong mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  className="w-full px-3 py-2 bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  placeholder="María"
                />
                {validationErrors.nombre && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.nombre}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-strong mb-1">
                  Correo electrónico *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  placeholder="tu@email.com"
                  disabled={!!firebaseUID || !!socialAuth} // Si ya viene de Firebase/Google/Apple, no permitir cambiar email
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-strong mb-1">
                  Teléfono *
                </label>
                <div className="flex gap-2">
                  <CountryCodeSelect
                    value={formData.phoneCountry}
                    onChange={handleCountrySelect}
                    disabled={loading}
                    error={!!validationErrors.telefono}
                  />
                  <input
                    type="tel"
                    inputMode="numeric"
                    required
                    value={formData.telefono}
                    onChange={(e) => {
                      const max = formData.phoneCountry === 'MX' ? 10 : 15;
                      handleInputChange('telefono', e.target.value.replace(/\D/g, '').slice(0, max));
                    }}
                    className="flex-1 min-w-0 px-3 py-2 bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                    placeholder={formData.phoneCountry === 'MX' ? '10 dígitos' : 'Número'}
                    maxLength={formData.phoneCountry === 'MX' ? 10 : 15}
                  />
                </div>
                {validationErrors.telefono && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.telefono}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || showEmailCheck || showPhoneCheck}
                className="w-full py-3 bg-daisies text-white rounded-card font-semibold hover:bg-daisies-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    {firebaseUID ? 'Creando tu cuenta...' : 'Registrando...'}
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    {firebaseUID ? 'Completar registro' : 'Crear cuenta'}
                  </>
                )}
              </button>

              <p className="text-xs text-text-mute text-center">
                Al crear tu cuenta aceptas los{' '}
                <a
                  href="https://daisies.mx/terminos-y-condiciones"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-daisies hover:text-daisies-hover underline font-semibold"
                >
                  términos y condiciones
                </a>
              </p>

              <div className="text-center pt-4 border-t border-brand-100">
                <button
                  type="button"
                  onClick={toggleStep}
                  className="text-daisies hover:text-daisies-hover text-sm font-medium"
                  disabled={loading}
                >
                  ¿Ya tienes cuenta? Inicia sesión aquí
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-brand-100">
            <div className="flex items-start gap-2 text-sm text-text-body">
              <CheckCircle className="text-mint-deep mt-0.5 flex-shrink-0" size={16} />
              <p>Tu información está protegida y solo se usa para gestionar tu reserva y cuenta Daisies.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;